// Import React
import React from 'react';

// Import Spectacle Core tags
import {
  BlockQuote,
  Cite,
  Deck,
  Heading,
  ListItem,
  List,
  Quote,
  Slide,
  Text,
  Appear,
  Fit,
  Image,
  Fill,
  Layout,
  Notes,
  S,
  CodePane,
} from 'spectacle';

import AppearItem from './components/AppearItem';

// Import theme
import createTheme from 'spectacle/lib/themes/default';

import preloader from 'spectacle/lib/utils/preloader';

const codeExampleLoader = require.context('!raw-loader!./code', false);

// Require CSS
require('normalize.css');

const theme = createTheme(
  {
    primary: '#30455b',
    secondary: '#FFFFFF',
    tertiary: '#f37523',
    quartenary: '#415a75',
    alternate: '#000000',
  },
  {
    primary: 'Montserrat',
    secondary: 'Helvetica',
  }
);

const images = {
  logo: require('./assets/hs-logo-white-small.png'),
  tinycat: require('./assets/tinycat.png'),
  bigcat: require('./assets/bigcat.jpg'),
  dotnet: require('./assets/dotnet.png'),
  java: require('./assets/java.png'),
  node: require('./assets/node.png'),
  go: require('./assets/go.png'),
  mindBlown: require('./assets/mind-blown.gif'),
  ninjaCat: require('./assets/ninjacat.png'),
  particles: require('./assets/particles.png'),
  account: require('./assets/hubspot/account.png'),
  dashboard: require('./assets/hubspot/dashboard.png'),
  profile: require('./assets/hubspot/profile.png'),
  settings: require('./assets/hubspot/settings.png'),
  master: require('./assets/hubspot/master.png'),
  masterdetail: require('./assets/hubspot/masterdetail.png'),
  navAndNotifications: require('./assets/hubspot/nav_and_notifications.png'),
  challengeAccepted: require('./assets/challengeaccepted.jpg'),
  gears: require('./assets/gears.jpg'),
  flash: require('./assets/flash.jpg'),
  supersonic: require('./assets/supersonic.png'),
  sharing: require('./assets/sharing.jpg'),
  library: require('./assets/library.jpg'),
  canvas: require('./assets/hubspot/canvas.png'),
  uiLibraryComponents: require('./assets/hubspot/ui-library-components.png'),
  uiLibraryPatterns: require('./assets/hubspot/ui-library-patterns.png'),
  migration: require('./assets/migration.jpg'),
  enterprise: require('./assets/enterprise.jpg'),
  mothershipTarget: require('./assets/hubspot/mothership-target.png'),
  mothershipTask: require('./assets/hubspot/mothership-task.png'),
  pullRequests: require('./assets/hubspot/pullrequests.png'),
  doctor: require('./assets/doctor.jpg'),
  balance: require('./assets/balance.jpg'),
  gifts: require('./assets/gifts.jpg'),
  orion: require('./assets/hubspot/orion.png'),
  minilla: require('./assets/hubspot/minilla.png'),
  benderProxy: require('./assets/hubspot/benderproxy.png'),
  robot: require('./assets/robot.jpg'),
  money: require('./assets/money.jpg'),
};

preloader(images);

export default class Presentation extends React.Component {
  render() {
    return (
      <Deck
        transition={['zoom', 'slide']}
        transitionDuration={500}
        theme={theme}
        progress="bar"
      >
        {/*
          Section: Introduction
        */}
        <Slide transition={['zoom']}>
          <Heading size={1} textSize="2em" caps textColor="secondary">
            Scaling the front-end
          </Heading>
          <Text bold textSize="1em" textColor="tertiary">
            How we build our product with micro-applications
          </Text>
          <Layout>
            <Fill>
              <Text
                textSize="1.5em"
                bold
                textColor="secondary"
                margin="58px auto auto auto"
              >
                Francois Ward
              </Text>
            </Fill>
            <Fill>
              <Image
                src={images.logo}
                height="2.1em"
                margin="50px 55px auto auto"
              />
            </Fill>
          </Layout>

          <Fit>
            <Text
              textSize="0.75em"
              bold
              textColor="secondary"
              margin="30px auto 0"
            >
              Tech Lead of Front-end Infrastructure
            </Text>
            <Text textSize="0.75em" bold textColor="secondary">
              @FwardPhoenix
            </Text>
            <Text textSize="0.75em" bold textColor="secondary">
              fward@hubspot.com
            </Text>
          </Fit>
        </Slide>
        {/*
          Section: The problem
        */}
        <Slide>
          <Heading fit textColor="secondary">
            Small projects are fun
          </Heading>
        </Slide>
        <Slide>
          <Image src={images.tinycat} height="8em" />
          <Heading fit textColor="secondary" caps>
            Benefits of being small
          </Heading>

          <List>
            <AppearItem>Build times are quick</AppearItem>
            <AppearItem>Small teams are easier</AppearItem>
            <AppearItem>There's not much tech debt (yet...)</AppearItem>
            <AppearItem>Fewer dependencies to think about</AppearItem>
            <AppearItem>Great place to experiment</AppearItem>
            <AppearItem>We can keep it all in our head</AppearItem>
          </List>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Too bad it doesn't stay that way
          </Heading>
          <Image src={images.bigcat} height="15em" margin="1em auto" />
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>Projects grow (that's kind of the point)</li>
              <li>Webpack starts struggling</li>
              <li>We start fighting with page size</li>
              <li>Refactoring becomes harder, riskier</li>
              <li>Deployments get slower and more complicated</li>
              <li>We start missing the good old days</li>
              <li>Even small companies get there pretty quick</li>
            </ol>
          </Notes>
        </Slide>
        <Slide bgColor="alternate">
          <Heading fit textColor="tertiary">
            Does it have to be this way?
          </Heading>
        </Slide>

        {/*
          Section: back-end
        */}
        <Slide bgColor="secondary">
          <Heading fit textColor="alternate" caps>
            Let's look at the back-end world
          </Heading>
          <Layout>
            <Fill>
              <Image src={images.dotnet} />
            </Fill>
            <Fill>
              <Image src={images.go} height="5em" />
            </Fill>
            <Fill>
              <Image src={images.java} height="5em" />
            </Fill>
            <Fill>
              <Image src={images.node} />
            </Fill>
          </Layout>
        </Slide>
        <Slide>
          <Text textSize="1.5em" textColor="tertiary">
            Incoming buzzword!
          </Text>
          <Appear>
            <Heading fit textColor="secondary" caps>
              Microservices
            </Heading>
          </Appear>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            First, let's define what that means
          </Heading>
        </Slide>
        <Slide bgColor="alternate">
          <BlockQuote>
            <Quote textSize="1em" textColor="secondary">
              Microservices is a variant of the service-oriented architecture
              (SOA) architectural style that structures an application as a
              collection of loosely coupled services. In a microservices
              architecture, services should be fine-grained and the protocols
              should be lightweight
            </Quote>
            <Cite textColor="tertiary">Wikipedia</Cite>
          </BlockQuote>
          <Appear>
            <Text textSize="1.5em" textColor="secondary">
              (Lot's of words to say nothing)
            </Text>
          </Appear>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            My definition of "Microservices"
          </Heading>
        </Slide>
        <Slide>
          <Heading fit bold caps textColor="secondary">
            What we used to do
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            We'll hand-wave a bit
          </Text>
          <List>
            <li>A system is monolithic</li>
            <li>Split in a couple of pieces</li>
            <li>Data, services, renderers, etc</li>
          </List>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Then DevOps happened
          </Heading>
          <Text textSize="1.5em" bold textColor="tertiary">
            Hello{' '}
            <S bold type="strikethrough">
              world!
            </S>{' '}
            the CLOUD!
          </Text>
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>The cloud </li>
              <li>Containers, dockers </li>
              <li>Infrastructure as a commodity service becomes possible</li>
            </ol>
          </Notes>
        </Slide>
        <Slide>
          <Heading textSize="1.5em" textColor="secondary" caps>
            We're not splitting services into 10 anymore
          </Heading>
        </Slide>
        <Slide>
          <Heading textSize="1.5em" textColor="secondary" caps>
            We're splitting them in hundreds or thousands
          </Heading>
          <Image src={images.mindBlown} />
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            But Back-end has an advantage
          </Heading>
          <Heading fit textColor="tertiary" caps>
            <Image src={images.ninjaCat} height="6em" />
            It's hidden. The customer never has to know
          </Heading>
        </Slide>
        <Slide bgColor="alternate">
          <Heading textSize="2em" textColor="secondary">
            How we get those same benefits on the FE at HubSpot
          </Heading>
          <List>
            <ListItem>Splitting the monolith</ListItem>
            <ListItem>Creating, building and deploying</ListItem>
            <ListItem>Streamlining development</ListItem>
            <ListItem>Style guide and libraries</ListItem>
            <ListItem>Migration and consistency</ListItem>
          </List>
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>Talk about HubSpot here</li>
            </ol>
          </Notes>
        </Slide>
        <Slide bgImage={images.particles} bgDarken={0.75}>
          <Heading fit textColor="secondary" caps>
            Let's take our app and split it
          </Heading>
          <Text bold textColor="tertiary">
            Not in 2, not in 10.{' '}
          </Text>
          <Text bold textColor="tertiary">
            Let's split it in 100s.
          </Text>
        </Slide>

        {/*
          Section: splitting an app
        */}
        <Slide>
          <Heading textSize="1.5em" textColor="secondary" caps>
            Start by keeping separate products separate (duh)
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            The blog doesn't have to be the same app as the calendar
          </Text>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Separate stand alone pieces
          </Heading>
          <Notes>
            <h4>Slides notes</h4>
            <ol>
              <li>User profile</li>
              <li>Settings</li>
              <li>Dashboards</li>
              <li>Help section</li>
              <li>Anything part of a single task, really</li>
            </ol>
          </Notes>
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="1.5em" textColor="secondary">
            An account page...
          </Text>
          <Image height="17em" src={images.account} margin="0.5em auto" />
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="1.5em" textColor="secondary">
            The user profile page...
          </Text>
          <Image height="17em" src={images.profile} margin="0.5em auto" />
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="1.5em" textColor="secondary">
            A settings page...
          </Text>
          <Image height="17em" src={images.settings} margin="0.5em auto" />
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="1.5em" textColor="secondary">
            A dashboard...
          </Text>
          <Image height="17em" src={images.dashboard} margin="0.5em auto" />
        </Slide>
        <Slide>
          <Heading textSize="2em" textColor="secondary" caps>
            Next, think about product sections
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            Index pages, tabs or rarely used screens
          </Text>
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="1.5em" textColor="secondary">
            This could be an app...
          </Text>
          <Image src={images.master} margin="0.5em auto" />
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="45px" textColor="secondary">
            And this could be a totally separate app
          </Text>
          <Image src={images.masterdetail} margin="0.5em auto" />
        </Slide>
        <Slide>
          <Heading size={3} textColor="secondary" caps>
            We have to think...
          </Heading>
          <Text textSize="1.5em" bold textColor="tertiary">
            Where are screen refreshes ok?
          </Text>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Stuff living every page
          </Heading>
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="1.5em" textColor="secondary">
            These are separate "apps"
          </Text>
          <Text textSize="1em" textColor="tertiary">
            We'll get into how we handle these later
          </Text>
          <Image src={images.navAndNotifications} margin="0.5em auto" />
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Almost every app has its own repo
          </Heading>
          <Text textColor="tertiary">
            (Though we certainly have monorepos too)
          </Text>
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>Micro repos vs mono repos</li>
              <li>
                Otherwise the talk would be "Use Lerna or Yarn workspaces. The
                end"
              </li>
              <li>This is what's going to make the biggest difference</li>
              <li>Talk about the distributed monolith</li>
              <li>Performance</li>
              <li>Simplifies build configuration</li>
              <li>Teams don't clash with each other</li>
              <li>The point is for teams to be independent</li>
            </ol>
          </Notes>
        </Slide>

        {/*
          Interlude
        */}
        <Slide>
          <Heading fit textColor="secondary" caps>
            Let's get technical
          </Heading>
          <Text bold textSize="1em" textColor="tertiary">
            How do we make this practical?
          </Text>
          <Image
            src={images.challengeAccepted}
            height="10em"
            margin="1em auto"
          />
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>Disclaimer: lots of HubSpot specific stuff</li>
              <li>Not meant to be an advertisement</li>
              <li>A lot of our stuff isn't open source</li>
              <li>(though we'd like it to be)</li>
              <li>The goal is to inspire to build these things</li>
            </ol>
          </Notes>
        </Slide>

        {/*
          Section: making new apps should be simple
        */}
        <Slide>
          <Heading textSize="2em" textColor="secondary" caps>
            Making new apps must be dead simple
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            All our new devs create one. Even if they don't know JavaScript
          </Text>
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>New devs build a full app from scratch</li>
              <li>Even those who don't know JavaScript</li>
            </ol>
          </Notes>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            A generator is the key
          </Heading>
          <List>
            <ListItem>Create a new app in minutes</ListItem>
            <ListItem>Enforce best practices</ListItem>
            <ListItem>Bring in our common libraries</ListItem>
            <ListItem>Make environment choice explicit</ListItem>
            <ListItem>Unit testing out of the box</ListItem>
            <ListItem>Webpack config for free</ListItem>
          </List>
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="1.5em" textColor="secondary">
            We use a yeoman generator
          </Text>
          <Text textSize="1em" textColor="tertiary">
            A fork of create-react-app works great too
          </Text>
        </Slide>

        {/*
          Section: builds
        */}
        <Slide bgImage={images.gears} bgDarken={0.75}>
          <Heading textColor="secondary" caps>
            Builds
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            Friction-free to avoid bottlenecks
          </Text>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Benefits of breaking the monolith
          </Heading>
          <List>
            <ListItem>Builds stay fast</ListItem>
            <ListItem>They more closely match with teams</ListItem>
            <ListItem>Other people don't break your stuff</ListItem>
            <ListItem>You don't break other people's</ListItem>
          </List>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Builds are automatic
          </Heading>
          <List>
            <ListItem>All repos/branches get built</ListItem>
            <ListItem>Builds happen on push</ListItem>
            <ListItem>The build itself is created automatically</ListItem>
            <ListItem>The generator includes the config file</ListItem>
            <ListItem>Configuration lives within the app</ListItem>
          </List>
          <Text textSize="1.5em" textColor="tertiary">
            Stuff most CI environments support these days
          </Text>
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>Jenkins or Travis support that too</li>
              <li>
                The goal is to not have to go in the build system to configure
                stuff
              </li>
            </ol>
          </Notes>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" capss>
            A sample build config
          </Heading>
          <CodePane
            lang="yaml"
            source={codeExampleLoader('./buildconfig.yaml')}
            margin="50px auto"
          />
          <Text textSize="1.5em" textColor="tertiary">
            Take a look at the publish section
          </Text>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" capss>
            Build artifacts are uploaded to S3
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            With every build
          </Text>
        </Slide>

        {/*
          Section: deploys
        */}
        <Slide>
          <Heading fit textColor="secondary" caps>
            Deploys are instant
          </Heading>
          <Image src={images.flash} />
          <Text textSize="1.5em" textColor="tertiary">
            (Ok, like 3 seconds)
          </Text>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            How to never wait on a deploy
          </Heading>
          <Image src={images.supersonic} height="9em" />
          <List>
            <ListItem>Files are already in S3</ListItem>
            <ListItem>We just point to the latest html</ListItem>
            <ListItem>Rollbacks are just as fast</ListItem>
          </List>
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="1.5em" textColor="secondary">
            I'm impatient
          </Text>
          <Image height="17em" src={images.orion} margin="1em auto" />
        </Slide>
        <Slide>
          <Heading textSize="2em" textColor="secondary" caps>
            Route configurations are per application
          </Heading>
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>Each team is responsible for their routes</li>
              <li>They're updated on deploy</li>
              <li>No need to coordinate with everyone</li>
            </ol>
          </Notes>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Publishing libraries
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            It's like an app, without the deploy
          </Text>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            We automatically host library assets
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            Linking or importing is the same
          </Text>
        </Slide>

        {/*
          Section: Running stuff locally
        */}
        <Slide bgImage={images.robot} bgDarken={0.75}>
          <Heading size={3} textColor="secondary" caps>
            How do we run that stuff locally?
          </Heading>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Bend and Asset Bender
          </Heading>
          <List>
            <ListItem>Always use the right version of Node.js</ListItem>
            <ListItem>One command to run all apps &amp; libraries</ListItem>
            <ListItem>We can still run apps on their own</ListItem>
            <ListItem>Because npm link is not fun</ListItem>
          </List>
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>Not much like that in open source</li>
              <li>Closest thing is Feross Zelda</li>
              <li>Doesn't help with apps though</li>
              <li>Shell script could do it</li>
            </ol>
          </Notes>
        </Slide>
        <Slide>
          <Heading textSize="2em" textColor="secondary" caps>
            Serving all those apps
          </Heading>
          <List>
            <ListItem>Local proxy</ListItem>
            <ListItem>Automatically route local apps</ListItem>
            <ListItem>Keeps local/qa/prod similar</ListItem>
            <ListItem>Use assets from CDN when needed</ListItem>
          </List>
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="45px" textColor="secondary">
            Zero configuration proxy
          </Text>
          <Image src={images.benderProxy} margin="70px auto" />
        </Slide>
        <Slide bgColor="alternate">
          <BlockQuote>
            <Quote textSize="1.5em" textColor="secondary">
              I should be able to pick up any project and run it locally without
              thinking about it
            </Quote>
          </BlockQuote>
        </Slide>

        {/*
          Section: consistency
        */}
        <Slide bgImage={images.library} bgDarken={0.75}>
          <Heading fit textColor="secondary" caps>
            Consistency
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            Eventually
          </Text>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Apps will not be in sync
          </Heading>
          <Text textSize="2em" textColor="tertiary">
            But we're ok with that
          </Text>
        </Slide>
        <Slide bgColor="alternate">
          <Heading fit textColor="secondary" caps>
            UX Consistency
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            Our secret sauce is our style guide
          </Text>
          <Image src={images.canvas} margin="2em auto" />
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>You can't under invest in a style guide</li>
              <li>It pays itself off in spades</li>
              <li>Even at smaller scale it's worth it</li>
              <li>Ours was a fork of something done for CRM</li>
            </ol>
          </Notes>
        </Slide>
        <Slide>
          <Heading textSize="2em" textColor="secondary" caps>
            The UI Library
          </Heading>
          <Text bold textSize="1.5em" textColor="tertiary">
            A catalog of components...
          </Text>
          <Image height="17em" src={images.uiLibraryComponents} />
        </Slide>
        <Slide>
          <Heading textSize="2em" textColor="secondary" caps>
            The UI Library
          </Heading>
          <Text bold textSize="1.5em" textColor="tertiary">
            ...but also of patterns
          </Text>
          <Image height="17em" src={images.uiLibraryPatterns} />
        </Slide>
        <Slide>
          <Heading textSize="1.5em" textColor="secondary" caps>
            You can't invest too much in a component style guide
          </Heading>
        </Slide>
        <Slide bgColor="alternate">
          <BlockQuote>
            <Quote textColor="secondary">
              I don't even remember CSS anymore!
            </Quote>
            <Cite textColor="tertiary">
              Several HubSpot developers, exaggerating just a little
            </Cite>
          </BlockQuote>
        </Slide>
        <Slide>
          <Heading textSize="2em" textColor="secondary" caps>
            Even our internal apps are pretty
          </Heading>
          <Image height="17em" src={images.minilla} />
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>The point is that the easy path is the right path</li>
              <li>It should just be that easy to make a great UI</li>
              <li>
                People shouldn't be wondering about margins and paddings all day
              </li>
            </ol>
          </Notes>
        </Slide>
        <Slide>
          <Heading textSize="2em" textColor="secondary" caps>
            Code consistency
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            Prettier &amp; ESLint
          </Text>
        </Slide>
        <Slide bgColor="alternate">
          <Heading textSize="2em" textColor="secondary" caps>
            Libraries are a pain
          </Heading>
        </Slide>
        <Slide>
          <Heading textSize="1.5em" textColor="secondary" caps>
            Sharing code between lots of environments is hard
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            The wrong abstraction is worse than no abstraction
          </Text>
        </Slide>
        <Slide>
          <Heading textColor="secondary" caps>
            Versioning
          </Heading>
          <List>
            <ListItem>Major and minor</ListItem>
            <ListItem>Apps always pick up latest minor</ListItem>
            <ListItem>Rebuilding the world is optional</ListItem>
            <ListItem>We don't automatically redeploy apps</ListItem>
            <ListItem>We do have tooling to nudge owners</ListItem>
          </List>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Some stuff MUST be consistent
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            You don't want a nav bar to be out of date
          </Text>
          <List>
            <ListItem>Injected scripts</ListItem>
            <ListItem>Good old iframes</ListItem>
            <ListItem>Configuration loaded via ajax</ListItem>
          </List>
        </Slide>
        <Slide>
          <Heading textSize="2em" textColor="secondary" caps>
            Webpack config consistency
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            We hide most of it
          </Text>
          <CodePane
            lang="js"
            source={codeExampleLoader('./webpackConfig.js')}
            margin="50px auto"
          />
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>
                Ok, most are more complicated than that for legacy reasons
              </li>
              <li>It's just a simple factory function</li>
              <li>
                If people messed with loaders, we'd lose the whole "I can run
                anyone's app without thinking about it"
              </li>
              <li>Library sharing would be harder (think NPM)</li>
            </ol>
          </Notes>
        </Slide>
        <Slide>
          <Heading textSize="2em" textColor="secondary" caps>
            Experimentation
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            Within reason
          </Text>
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>Since apps are independent its safe</li>
              <li>But we have to balance out consistency</li>
              <li>It's a challenge</li>
              <li>"Blessed stack" more of a convention</li>
            </ol>
          </Notes>
        </Slide>

        {/*
          Section: Migrations
        */}
        <Slide bgImage={images.migration} bgDarken={0.75}>
          <Heading fit textColor="secondary" caps>
            Migrations
          </Heading>
          <Text textSize="1.5em" bold textColor="tertiary" caps>
            How to keep hundreds of apps and repos consistent
          </Text>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Automate, automate, automate
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            Opening 100 pull requests at once should feel normal
          </Text>
        </Slide>
        <Slide>
          <Heading textColor="secondary" caps>
            Mothership
          </Heading>
          <Image src={images.enterprise} />
          <Text bold textSize="1.5em" textColor="tertiary">
            A cross project task runner
          </Text>
          <List>
            <ListItem>Can be used for self service</ListItem>
            <ListItem>GUI to coordinate "missions"</ListItem>
            <ListItem>Updating projects at scale</ListItem>
          </List>
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="45px" textColor="secondary">
            We pick a target...
          </Text>
          <Image
            height="17em"
            src={images.mothershipTarget}
            margin="70px auto"
          />
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="45px" textColor="secondary">
            Then a task...
          </Text>
          <Image src={images.mothershipTask} margin="70px auto" />
        </Slide>
        <Slide bgColor="alternate">
          <Text textSize="45px" textColor="secondary">
            Then sit back and watch the fireworks
          </Text>
          <Image height="17em" src={images.pullRequests} margin="70px auto" />
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Codemods are our friends
          </Heading>
          <Text textSize="1.5em" bold textColor="tertiary" caps>
            We love jscodeshift
          </Text>
        </Slide>
        <Slide>
          <Heading textSize="2em" textColor="secondary" caps>
            Project health should be a first class citizen
          </Heading>
          <Image src={images.doctor} margin="70px auto" />
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Cross team efforts are hard
          </Heading>
          <Text textSize="1.5em" bold textColor="tertiary" caps>
            So we try to make them easier
          </Text>
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>
                Cross team coordination is harder, but within teams its easier
              </li>
              <li>There's still a lot of work to do here</li>
            </ol>
          </Notes>
        </Slide>

        {/*
          Section: When the cost is too high
        */}
        <Slide bgImage={images.money} bgDarken={0.75}>
          <Heading textSize="2em" textColor="secondary" caps>
            But what if the cost is too high?
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            What to do when we just can't split?
          </Text>
        </Slide>
        <Slide bgColor="alternate">
          <Heading textSize="2em" textColor="secondary" caps>
            We're still figuring that out
          </Heading>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Some possible strategies
          </Heading>
          <List>
            <ListItem>Isolated injected scripts</ListItem>
            <ListItem>iframes</ListItem>
            <ListItem>Webpack's DllPlugin</ListItem>
          </List>
          <Text textSize="1.5em" textColor="tertiary">
            The goal is to not solve for all architectures
          </Text>
        </Slide>

        {/*
          Section: Tradeoffs
        */}
        <Slide bgImage={images.balance} bgDarken={0.75}>
          <Heading fit textColor="secondary" caps>
            Tradeoffs
          </Heading>
          <Heading fit textColor="tertiary" caps>
            Because no architecture is perfect
          </Heading>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            Some issues we deal with...
          </Heading>
          <List>
            <ListItem>Pages do refresh</ListItem>
            <ListItem>Not as seamless as a typical SPA</ListItem>
            <ListItem>Repeated code</ListItem>
            <ListItem>Not suitable for smaller systems</ListItem>
            <ListItem>Workflow can be unfamiliar</ListItem>
            <ListItem>Not as much community support</ListItem>
          </List>
        </Slide>

        {/*
          Section: It's worth it
        */}
        <Slide>
          <Heading fit textColor="secondary" caps>
            We think it's worth it
          </Heading>
          <List>
            <ListItem>We get the benefits of small projects</ListItem>
            <ListItem>Teams can be autonomous</ListItem>
            <ListItem>We don't need to scale everything</ListItem>
          </List>
        </Slide>

        {/*
          Section: Wrapping it up
        */}
        <Slide bgImage={images.gifts} bgDarken={0.75}>
          <Heading fit textColor="secondary" caps>
            Wrapping it up
          </Heading>
        </Slide>

        <Slide>
          <Heading fit textColor="secondary" caps>
            "Micro-services" on the F-E
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            Dealing with growing pains by avoiding them via infrastructure
          </Text>
        </Slide>
        <Slide>
          <Heading fit textColor="secondary" caps>
            It's not for everyone
          </Heading>
          <Text textSize="1.5em" textColor="tertiary">
            But it's a great option when you need to scale
          </Text>
        </Slide>

        {/*
          Section: Thanks
        */}
        <Slide>
          <Heading textColor="secondary" caps>
            Thanks!
          </Heading>
          <Text bold textSize="1.5em" textColor="tertiary">
            I want to hear from you!
          </Text>
          <Text textColor="secondary">@FwardPhoenix</Text>
          <Text textColor="secondary">fward@hubspot.com</Text>
          <Text textColor="secondary">bostonjavascript.slack.com</Text>
          <Notes>
            <h4>Slide notes</h4>
            <ol>
              <li>Interested in hearing from people who do similar things</li>
              <li>
                Maybe not exactly like that, but use tech that would fit in this
                model
              </li>
              <li>How do we minimize UX impact</li>
            </ol>
          </Notes>
        </Slide>
        <Slide bgColor="secondary">
          <Heading textSize="2em" textColor="alternate" margin="1em auto">
            Attributions
          </Heading>
          <Text textAlign="left" textSize="1em" textColor="alternate">
            <a href="https://www.flickr.com/photos/ryanr/142455033">Sharing</a>{' '}
            by ryancr, flickr.{' '}
            <a href="https://creativecommons.org/licenses/by-nc/2.0/legalcode">
              CC BY-NC 2.0
            </a>{' '}
          </Text>
          <Text textAlign="left" textSize="1em" textColor="alternate">
            <a href="https://commons.wikimedia.org/wiki/File:Sumatran_Tiger_5_(6964685356).jpg">
              Tiger
            </a>{' '}
            by Tony Hisgett.{' '}
            <a href=" https://creativecommons.org/licenses/by/2.0">CC BY 2.0</a>{' '}
          </Text>
          <Text textAlign="left" textSize="1em" textColor="alternate">
            <a href="https://ccsearch.creativecommons.org/image/detail/7qRx_yrcN5isTMS0u9iKMA==">
              Gears
            </a>{' '}
            by Andrew Magill.{' '}
            <a href="https://creativecommons.org/licenses/by/2.0">CC BY 2.0</a>{' '}
          </Text>
          <Text textAlign="left" textSize="1em" textColor="alternate">
            <a href="https://www.flickr.com/photos/fire_brace/2168226355/">
              Particles
            </a>{' '}
            by Keith Hall.{' '}
            <a href="https://creativecommons.org/licenses/by/2.0">CC BY 2.0</a>{' '}
          </Text>
          <Text textAlign="left" textSize="1em" textColor="alternate">
            <a href="https://ccsearch.creativecommons.org/image/detail/vIDg7K8KxxrLF-2Ja0mA7g==">
              Books
            </a>{' '}
            by Neil H.{' '}
            <a href="https://creativecommons.org/licenses/by/2.0">CC BY 2.0</a>{' '}
          </Text>
          <Text textAlign="left" textSize="1em" textColor="alternate">
            <a href="https://ccsearch.creativecommons.org/image/detail/pS5qj3cWUZR4CZRRlwriQg==">
              Migration
            </a>{' '}
            by Ben Salter{' '}
            <a href="https://creativecommons.org/licenses/by/2.0">CC BY 2.0</a>{' '}
          </Text>
          <Text textAlign="left" textSize="1em" textColor="alternate">
            <a href="https://www.flickr.com/photos/arrhakis/16887659087">
              The Nebula Worlds Of Arhum - An Homage To Spock
            </a>{' '}
            by Daniel Arrhakis{' '}
            <a href="https://creativecommons.org/licenses/by-nc/2.0/legalcode">
              CC BY-NC 2.0
            </a>{' '}
          </Text>
          <Text textAlign="left" textSize="1em" textColor="alternate">
            <a href="https://www.flickr.com/photos/jeffeaton/6586675253">
              Doctor
            </a>{' '}
            by Jeff Eaton{' '}
            <a href="https://creativecommons.org/licenses/by-sa/2.0/legalcode">
              CC BY-SA 2.0
            </a>{' '}
          </Text>
          <Text textAlign="left" textSize="1em" textColor="alternate">
            <a href="https://www.flickr.com/photos/jeffeaton/6586675253">
              Balance
            </a>{' '}
            Hans Splinter{' '}
            <a href="https://creativecommons.org/licenses/by-nd/2.0/legalcode">
              CC BY-ND 2.0
            </a>{' '}
          </Text>
          <Text textAlign="left" textSize="1em" textColor="alternate">
            <a href="https://www.flickr.com/photos/jeffeaton/6586675253">
              empty gifts and promises
            </a>{' '}
            torbakhopper{' '}
            <a href="https://creativecommons.org/licenses/by/2.0/legalcode">
              CC BY 2.0
            </a>{' '}
          </Text>
          <Text textAlign="left" textSize="1em" textColor="alternate">
            <a href="https://www.flickr.com/photos/127771812@N05/15089377110/">
              Robot
            </a>{' '}
            knowmadic news{' '}
            <a href="https://creativecommons.org/licenses/by-sa/2.0/">
              CC BY-SA 2.0
            </a>{' '}
          </Text>
          <Text textAlign="left" textSize="1em" textColor="alternate">
            <a href="https://www.flickr.com/photos/cooperweb/8363160192/">
              Money
            </a>{' '}
            Keith Cooper{' '}
            <a href="https://creativecommons.org/licenses/by/2.0/legalcode">
              CC BY 2.0
            </a>{' '}
          </Text>
          <Text textSize="0.5em">HubSpot screenshots taken by myself</Text>
          <Text textSize="0.5em">Other images used in fair use</Text>
        </Slide>
      </Deck>
    );
  }
}



// WEBPACK FOOTER //
// ./src/presentation.js